import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Store, Users, Package, BadgeDollarSign, ArrowUp, ArrowDown, Calendar, ChevronDown, MoreVertical, ChevronRight, User, Bike } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, LabelList, CartesianGrid } from 'recharts';
import { useApp } from '../context/AppContext';
import { money } from '../lib/constants';
import { Card, Stat, StatusBadge, Avatar, Table, Tr, Td } from './shared';
import { LiveBusinessOrders } from './LiveBusinessOrders';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

const Growth = ({ pct }) => (
  <div className="mt-0.5">
    <span className={`inline-flex items-center gap-0.5 font-semibold text-sm ${pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{pct >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />} {Math.abs(pct)}%</span>
    <p className="text-xs text-gray-500">vs. mes anterior</p>
  </div>
);

const RangeSelect = ({ value = 'month' }) => (
  <Select defaultValue={value}>
    <SelectTrigger className="h-9 w-[130px] rounded-lg text-sm bg-white"><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="week">Esta semana</SelectItem><SelectItem value="month">Este mes</SelectItem><SelectItem value="year">Este año</SelectItem></SelectContent>
  </Select>
);

const kindIcon = { restaurant: { icon: Store, bg: 'bg-[#FDECEC] text-[#EE3B36]' }, driver: { icon: User, bg: 'bg-[#E8F0FE] text-[#2F80ED]' }, user: { icon: User, bg: 'bg-[#E6F7F1] text-[#22A45D]' } };
const kindLink = { restaurant: '/admin/restaurantes', driver: '/admin/conductores', user: '/admin/usuarios' };

const AdminDashboard = () => {
  const { orders, stats } = useApp();
  if (!stats) return <div className="space-y-4"><Skeleton className="h-12 w-96" /><div className="grid grid-cols-2 xl:grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div><Skeleton className="h-64 rounded-2xl" /></div>;
  const t = stats.totals;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <div>
          <h1 className="text-3xl md:text-[34px] font-extrabold text-[#1E2130] tracking-tight">Panel de Administrador</h1>
          <p className="text-gray-500 text-lg mt-0.5">Bienvenido al panel de control de Delibery 7</p>
        </div>
        <button className="flex items-center gap-3 h-11 px-4 rounded-xl bg-white border border-gray-200 text-sm font-medium hover:border-gray-300" data-testid="date-range">
          <Calendar size={18} className="text-[#1E2130]" /> {stats.range} <ChevronDown size={16} className="text-gray-500" />
        </button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
        <Stat label="Pedidos Totales" value={t.orders} icon={ShoppingCart} bg="bg-[#FDECEC]" iconBg="bg-[#F9D4D2] text-[#EE3B36]" sub={<Growth pct={t.ordersGrowth} />} />
        <Stat label="Restaurantes" value={t.restaurants} icon={Store} bg="bg-[#E6F7F1]" iconBg="bg-[#CDEFE0] text-[#22A45D]" sub={<Growth pct={t.restaurantsGrowth} />} />
        <Stat label="Conductores" value={t.drivers} icon={Users} bg="bg-[#E8F0FE]" iconBg="bg-[#D2E1FC] text-[#2F80ED]" sub={<Growth pct={t.driversGrowth} />} />
        <Stat label="Mandaditos 7" value={t.mandaditos} icon={Package} bg="bg-[#FFF6E0]" iconBg="bg-[#FFE8B8] text-[#F08A00]" sub={<Growth pct={t.mandaditosGrowth} />} />
        <Stat label="Ingresos (L)" value={money(t.revenue)} icon={BadgeDollarSign} bg="bg-[#F1ECFD]" iconBg="bg-[#E2D8FB] text-[#7C3AED]" sub={<Growth pct={t.revenueGrowth} />} />
      </div>

      <div className="grid xl:grid-cols-[1.6fr_1fr_1fr] gap-4 mt-4">
        <Card title="Pedidos por día" action={<RangeSelect />}>
          <div className="h-[210px] px-2 pb-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.ordersPerDay} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                <defs><linearGradient id="redFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EE3B36" stopOpacity={0.25} /><stop offset="100%" stopColor="#EE3B36" stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid vertical={false} stroke="#EEF0F3" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} interval={4} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #eee', fontSize: 12 }} />
                <Area type="linear" dataKey="value" stroke="#EE3B36" strokeWidth={2} fill="url(#redFill)" dot={{ r: 3, fill: '#EE3B36', strokeWidth: 0 }} activeDot={{ r: 5 }} name="Pedidos" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Tipos de servicios">
          <div className="flex items-center gap-2 px-4 pb-4">
            <div className="relative w-[150px] h-[150px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={stats.serviceTypes} dataKey="value" innerRadius={50} outerRadius={72} startAngle={90} endAngle={-270} paddingAngle={2} strokeWidth={0}>{stats.serviceTypes.map((s) => <Cell key={s.name} fill={s.color} />)}</Pie></PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><p className="text-2xl font-extrabold text-[#1E2130] leading-none">{t.orders}</p><p className="text-xs text-gray-500">Pedidos</p></div>
            </div>
            <ul className="flex-1 space-y-3">
              {stats.serviceTypes.map((s) => (
                <li key={s.name} className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: s.color }} /> {s.name}</span><span className="font-semibold">{s.value}%</span></li>
              ))}
            </ul>
          </div>
        </Card>

        <Card title="Estado de pedidos">
          <ul className="px-4 pb-4 space-y-2">
            {stats.orderStates.map((s) => (
              <li key={s.label} className="flex items-center justify-between text-sm bg-[#F7F8FA] rounded-lg px-3 py-2.5"><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /> {s.label}</span><span className="flex items-center gap-4"><span className="font-bold">{s.count}</span><span className="text-gray-500 w-8 text-right">{s.pct}</span></span></li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid xl:grid-cols-[2.7fr_1fr] gap-4 mt-4">
        <Card title="Últimos pedidos" action={<Link to="/admin/pedidos" className="text-sm font-semibold text-[#2F80ED] hover:underline">Ver todos</Link>}>
          <div className="px-3 pb-3">
            <Table columns={['# Pedido', 'Cliente', 'Tipo', 'Restaurante / Origen', 'Total', 'Estado', 'Hora', 'Acciones']} rows={orders.slice(0, 5)}
              renderRow={(o) => (
                <Tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <Td className="font-semibold">#{o.id}</Td>
                  <Td><span className="flex items-center gap-2"><Avatar src={o.avatar} name={o.customer} size={30} />{o.customer}</span></Td>
                  <Td>{o.type}</Td><Td>{o.restaurant}</Td>
                  <Td className="font-semibold">{money(o.total)}</Td>
                  <Td><StatusBadge status={o.status} /></Td>
                  <Td className="text-gray-600">{o.time}</Td>
                  <Td><DropdownMenu><DropdownMenuTrigger asChild><button className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50"><MoreVertical size={16} /></button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem asChild><Link to="/admin/pedidos">Ver detalle</Link></DropdownMenuItem></DropdownMenuContent></DropdownMenu></Td>
                </Tr>
              )} />
          </div>
        </Card>

        <Card title="Nuevos registros" action={<Link to="/admin/usuarios" className="text-sm font-semibold text-[#2F80ED] hover:underline">Ver todos</Link>}>
          <ul className="px-3 pb-3 space-y-2">
            {stats.newRegistrations.map((r) => { const k = kindIcon[r.kind]; return (
              <Link key={r.id} to={kindLink[r.kind]} className="flex items-center gap-3 bg-[#F7F8FA] rounded-xl px-3 py-2.5 hover:bg-[#F1F2F5] transition-colors">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.bg}`}><k.icon size={20} /></div>
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{r.type} - {r.name}</p><p className="text-xs text-gray-500">{r.sub}</p></div>
                <ChevronRight size={18} className="text-gray-400" />
              </Link>); })}
            {stats.newRegistrations.length === 0 && <li className="text-sm text-gray-400 text-center py-6">Sin registros nuevos</li>}
          </ul>
        </Card>
      </div>

      <div className="grid xl:grid-cols-[1.35fr_1fr_1fr] gap-4 mt-4">
        <Card title="Ingresos por semana" action={<RangeSelect />}>
          <div className="h-[210px] px-2 pb-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.revenuePerWeek} margin={{ top: 24, right: 12, left: -10, bottom: 0 }} barSize={54}>
                <CartesianGrid vertical={false} stroke="#EEF0F3" />
                <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip cursor={{ fill: '#F7F8FA' }} contentStyle={{ borderRadius: 12, border: '1px solid #eee', fontSize: 12 }} formatter={(v) => money(v)} />
                <Bar dataKey="value" fill="#2F80ED" radius={[4, 4, 0, 0]} name="Ingresos"><LabelList dataKey="value" position="top" formatter={(v) => money(v)} style={{ fontSize: 12, fontWeight: 600, fill: '#1E2130' }} /></Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Restaurantes más activos" action={<Link to="/admin/restaurantes" className="text-sm font-semibold text-[#2F80ED] hover:underline">Ver todos</Link>}>
          <ul className="px-4 pb-4">
            {stats.topRestaurants.map((r, i) => (
              <li key={r.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"><span className="w-5 text-sm font-bold text-[#1E2130]">{i + 1}</span><img src={r.image} alt="" className="w-8 h-8 rounded-lg object-cover" /><span className="flex-1 text-sm font-medium truncate">{r.name}</span><span className="text-xs text-gray-500">{r.orders} pedidos</span></li>
            ))}
          </ul>
        </Card>

        <Card title="Conductores con más entregas" action={<Link to="/admin/conductores" className="text-sm font-semibold text-[#2F80ED] hover:underline">Ver todos</Link>}>
          <ul className="px-4 pb-4">
            {stats.topDrivers.map((d, i) => (
              <li key={d.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0"><span className="w-5 text-sm font-bold text-[#1E2130]">{i + 1}</span><Avatar src={d.avatar} name={d.name} size={32} /><span className="flex-1 text-sm font-medium truncate">{d.name}</span><span className="text-xs text-gray-500 flex items-center gap-1"><Bike size={12} /> {d.deliveries} entregas</span></li>
            ))}
          </ul>
        </Card>
      </div>
      <div className="mt-6"><LiveBusinessOrders compact /></div>
    </div>
  );
};

export default AdminDashboard;
